import React, { useState } from 'react';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { BaseDialog } from 'src/components/BaseDialog/BaseDialog';
import { useCreatePostItMutation, useUpdatePostItMutation } from 'src/api/v1/postits-ApiSlice';
import { ts } from 'src/utils/translate';
import { NoteAddToDevice, NoteDrop, NoteEdit, NoteEditOnDevice } from 'src/pages/PlannerPage/PlannerPage';
import {
    applyCreatedDevNote,
    applyCreatedNote,
    applyUpdatedDevNote,
    applyUpdatedNote,
} from 'src/pages/PlannerPage/Planner/dialogs/AddNoteDialog/addNoteDialogActions';

export const NOTE_COLORS = [
    '#FEF3C7',
    '#FCE7F3',
    '#DCFCE7',
    '#DBEAFE',
    '#F3E8FF',
    '#FEE2E2',
] as const;

enum NoteType {
    Alone = 1,
    AttachedPlanObject = 2,
    AttachedUserCustomPlanObject = 3,
}

const CUSTOM_DEVICE_ID_PREFIX = 'C_';

function deviceAttachPayload(deviceId: string | number): { noteType: NoteType, planObjectId: number } {
    const deviceIdStr = String(deviceId);
    const isCustom = deviceIdStr.startsWith(CUSTOM_DEVICE_ID_PREFIX);
    return {
        noteType: isCustom
            ? NoteType.AttachedUserCustomPlanObject
            : NoteType.AttachedPlanObject,
        planObjectId: Number(deviceIdStr.replace(CUSTOM_DEVICE_ID_PREFIX, '')),
    };
}

export interface AddNoteDialogProps {
    noteDrop?: NoteDrop;
    noteEdit?: NoteEdit;
    noteEditOnDevice?: NoteEditOnDevice;
    noteAddToDevice?: NoteAddToDevice;
    onClose: () => void;
}

export function AddNoteDialog({
    noteDrop,
    noteEdit,
    noteEditOnDevice,
    noteAddToDevice,
    onClose,
}: AddNoteDialogProps) {
    const isEdit = noteEdit != null || noteEditOnDevice != null;
    const initialText = noteEdit?.text ?? noteEditOnDevice?.text ?? '';
    const initialColor = noteEdit?.color ?? noteEditOnDevice?.color ?? NOTE_COLORS[0];
    const [text, setText] = useState(initialText);
    const [color, setColor] = useState<string>(initialColor);
    const [createPostIt, { isLoading: isCreating }] = useCreatePostItMutation();
    const [updatePostIt, { isLoading: isUpdating }] = useUpdatePostItMutation();
    const isLoading = isCreating || isUpdating;

    const handleConfirm = () => {
        const trimmedText = text.trim();
        if (!trimmedText) return;
        if (noteEdit) {
            updatePostIt({
                post_it_id: noteEdit.id,
                color,
                note: trimmedText,
                x: noteEdit.x,
                y: noteEdit.y,
            }).then((response) => {
                if (!response.data) return;
                applyUpdatedNote(
                    {
                        noteId: noteEdit.id,
                        x: noteEdit.x,
                        y: noteEdit.y,
                        text: trimmedText,
                        color,
                    },
                    { text: noteEdit.text, color: noteEdit.color },
                );
                onClose();
            });
        } else if (noteEditOnDevice) {
            updatePostIt({
                post_it_id: noteEditOnDevice.id,
                color,
                note: trimmedText,
            }).then((response) => {
                if (!response.data) return;
                applyUpdatedDevNote(
                    {
                        deviceId: noteEditOnDevice.deviceId,
                        text: trimmedText,
                        color,
                    },
                    { text: noteEditOnDevice.text, color: noteEditOnDevice.color },
                );
                onClose();
            });
        } else if (noteDrop) {
            createPostIt({
                x: noteDrop.x,
                y: noteDrop.y,
                color,
                note: trimmedText,
                floor_id: noteDrop.floorId,
                note_type: NoteType.Alone,
            }).then((response) => {
                if (!response.data) return;
                applyCreatedNote({
                    noteId: response.data.id,
                    x: noteDrop.x,
                    y: noteDrop.y,
                    text: trimmedText,
                    color,
                });
                onClose();
            });
        } else if (noteAddToDevice) {
            const { noteType, planObjectId } = deviceAttachPayload(noteAddToDevice.deviceId);
            createPostIt({
                color,
                note: trimmedText,
                floor_id: noteAddToDevice.floorId,
                note_type: noteType,
                plan_object_id: planObjectId,
            }).then((response) => {
                if (!response.data) return;
                applyCreatedDevNote({
                    noteId: response.data.id,
                    deviceId: noteAddToDevice.deviceId,
                    text: trimmedText,
                    color,
                });
                onClose();
            });
        }
    };

    const confirmDisabled = text.trim().length === 0 || isLoading;

    return (
        <BaseDialog
            open
            onClose={onClose}
            title={isEdit
                ? ts('add_note_modify_title', 'Modify note')
                : ts('add_note_add_title', 'Add note')}
            maxWidth="xs"
            actions={
                <Stack direction="row" spacing={2}>
                    <Button variant="outlined" onClick={onClose} sx={{ minWidth: 100 }}>
                        {ts('add_note_cancel', 'Cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirm}
                        disabled={confirmDisabled}
                        sx={{ minWidth: 100 }}
                    >
                        {ts('add_note_confirm', 'Confirm')}
                    </Button>
                </Stack>
            }
        >
            <Stack spacing={2}>
                <Box>
                    <Typography
                        variant="caption"
                        sx={{ fontWeight: 600, letterSpacing: 1, color: 'text.secondary' }}
                    >
                        {ts('add_note_section_note', 'NOTE')}
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        minRows={6}
                        placeholder={ts('add_note_placeholder', 'Type your note here...')}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        autoFocus
                        sx={{ mt: 0.5 }}
                    />
                </Box>

                <Box>
                    <Typography
                        variant="caption"
                        sx={{ fontWeight: 600, letterSpacing: 1, color: 'text.secondary' }}
                    >
                        {ts('add_note_select_color', 'SELECT COLOR')}
                    </Typography>
                    <Stack direction="row" spacing={1.5} sx={{ mt: 1 }}>
                        {NOTE_COLORS.map((c) => (
                            <Box
                                key={c}
                                onClick={() => setColor(c)}
                                sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: '50%',
                                    backgroundColor: c,
                                    cursor: 'pointer',
                                    border: color === c ? '2px solid' : '2px solid transparent',
                                    borderColor: color === c ? 'primary.main' : 'transparent',
                                }}
                            />
                        ))}
                    </Stack>
                </Box>
            </Stack>
        </BaseDialog>
    );
}
